import { scrollToElementById, shakeElementById } from "@utils/utils";
import React from "react";
import { useFormContext } from "react-hook-form";

import { Label, PopoverPanel } from "@fronterahealth/frontera-ui-components";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";

import { useFocusedFieldManager } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { IncompleteNarrativeField } from "@pages/ClinicalReportDetails/ReportPage/hooks/useReassessmentApprovalProgress";

interface ReassessmentApprovalProgressIndicatorProps {
  approvedGoals: number;
  totalGoals: number;
  approvedTargets: number;
  totalTargets: number;
  completedNarrativeFields: number;
  totalNarrativeFields: number;
  incompleteNarrativeFields: IncompleteNarrativeField[];
  percentage: number;
}

/**
 * Bordered, clickable approval-progress chip that lives in the Reassessment
 * step-progress header, to the left of the Previous/Next CTAs. Click opens
 * a popover (downward, anchored to the right edge of the chip) showing the
 * goal/target breakdown. Progress data is passed in from the parent so the
 * underlying hook only runs once per render.
 */
export const ReassessmentApprovalProgressIndicator: React.FC<ReassessmentApprovalProgressIndicatorProps> = ({
  approvedGoals,
  totalGoals,
  approvedTargets,
  totalTargets,
  completedNarrativeFields,
  totalNarrativeFields,
  incompleteNarrativeFields,
  percentage,
}) => {
  const focusManager = useFocusedFieldManager();
  const form = useFormContext<Record<string, string>>();
  const first = incompleteNarrativeFields[0];

  const revalidateRequiredFields = form?.handleSubmit(
    () => {},
    () => {},
  );

  const goToFirstIncompleteField = () => {
    if (!first) return;
    void revalidateRequiredFields?.();
    focusManager?.setFocused(first.id);
    focusManager?.setShowRequiredErrors(true);
    scrollToElementById(first.id, "center");
    shakeElementById(first.id);
  };

  if (totalGoals + totalTargets + totalNarrativeFields === 0) return null;

  const popoverContent = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Label size="small" displayType="loud" colorType="primary">
          Report Progress
        </Label>
        <div className="flex items-center gap-2">
          <div className="h-1 flex-1 rounded-full bg-limestone-100">
            <div className="h-1 rounded-full bg-icon-success transition-all" style={{ width: `${percentage}%` }} />
          </div>
          <Label size="extra-small" displayType="loud" colorType="primary" className="whitespace-nowrap">
            {percentage}% complete
          </Label>
        </div>
      </div>

      <div className="flex flex-col gap-2 text-xs">
        {totalGoals > 0 && (
          <div className="flex items-center justify-between">
            <Label size="extra-small" colorType="secondary">
              Goals approved
            </Label>
            <Label size="extra-small" colorType="primary">
              {approvedGoals} of {totalGoals}
            </Label>
          </div>
        )}
        {totalTargets > 0 && (
          <div className="flex items-center justify-between">
            <Label size="extra-small" colorType="secondary">
              Targets approved
            </Label>
            <Label size="extra-small" colorType="primary">
              {approvedTargets} of {totalTargets}
            </Label>
          </div>
        )}
        {totalNarrativeFields > 0 && (
          <div className="flex items-center justify-between">
            <Label size="extra-small" colorType="secondary">
              Required fields completed
            </Label>
            <Label size="extra-small" colorType="primary">
              {completedNarrativeFields} of {totalNarrativeFields}
            </Label>
          </div>
        )}
        {incompleteNarrativeFields.length > 0 && (
          <Button variant="ghost" size="sm" onClick={goToFirstIncompleteField}>
            Highlight incomplete fields
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <PopoverPanel direction="bottom-left" width="w-[296px]" content={popoverContent}>
      <button
        type="button"
        aria-label={`Approval progress: ${percentage}%`}
        className="flex items-center gap-2 rounded-full border border-border-subtle px-2 py-1 transition-colors hover:bg-limestone-50 focus:outline-hidden"
      >
        <ProgressSpinner percentage={percentage} />
        <span className="text-sm tabular-nums text-text-primary">{percentage}%</span>
      </button>
    </PopoverPanel>
  );
};

const ProgressSpinner: React.FC<{ percentage: number }> = ({ percentage }) => {
  const radius = 6;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r={radius} fill="none" stroke="#EDEAE7" strokeWidth="2" />
      <circle
        cx="8"
        cy="8"
        r={radius}
        fill="none"
        stroke="#16A34A"
        strokeWidth="2"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform="rotate(-90 8 8)"
      />
    </svg>
  );
};
